import { Tooltip } from '@mui/material';
import { formatPrice, getPriceChangeIcon } from '../utils';
import { StationFuelPriceCellProps } from './StationFuelPriceCell.types';

/**
 * Renders a fuel price cell for station-level pricing data
 * Shows different layouts for OKKO stations (with sales data) vs competitors (price only)

 */
export function StationFuelPriceCell({ fuelType, fuelPrice, isOkko, cellClass = '', hideSales = false }: StationFuelPriceCellProps) {
  const price = fuelType === 'SPBT' ? fuelPrice.average : fuelPrice.mode;
  const priceChange = fuelPrice.priceChange;
  const priceChangeValue = fuelPrice.priceChangeValue ?? 0;
  const weekGrowth = fuelPrice.sales?.weekGrowth ?? null;
  const dayGrowth = fuelPrice.sales?.dayGrowth ?? 0;

  return (
    <div
      className={`regional-fuel-container ${cellClass} ${!isOkko ? (priceChange === 'new' ? 'price-new' : priceChange === 'removed' ? 'price-removed' : '') : ''}`}
    >
      {isOkko && fuelPrice.sales ? (
        <div className="regional-sales-layout">
          <Tooltip
            title={<div style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>{`Ціна ${fuelType}`}</div>}
            placement="top"
            enterDelay={200}
            leaveDelay={100}
          >
            <div
              className={`regional-sales-current ${priceChange === 'new' ? 'price-new' : priceChange === 'removed' ? 'price-removed' : ''}`}
            >
              {priceChange === 'removed' ? (
                ''
              ) : (
                <>
                  <span>{formatPrice(price)}</span>
                  {priceChangeValue !== 0 && Math.abs(priceChangeValue) > 0.001 && (
                    <span className={priceChangeValue >= 0 ? 'price-diff-positive' : 'price-diff-negative'}>
                      ({priceChangeValue >= 0 ? '+' : ''}
                      {priceChangeValue.toFixed(2)})
                      <span
                        className={priceChange === 'up' ? 'price-arrow up' : priceChange === 'down' ? 'price-arrow down' : ''}
                      >
                        {getPriceChangeIcon(priceChange)}
                      </span>
                    </span>
                  )}
                </>
              )}
            </div>
          </Tooltip>
          {!hideSales && (
            <div className="regional-sales-bottom">
            <div className="regional-sales-left">
              <Tooltip
                title={
                  <div
                    style={{ whiteSpace: 'pre-line', textAlign: 'center' }}
                  >{`Срдн. реал. по ${fuelType} за\nпот. тиж.`}</div>
                }
                placement="top"
                enterDelay={200}
                leaveDelay={100}
              >
                <div className="regional-sales-value">
                  {fuelPrice.sales.currentWeekAvg
                    ? fuelPrice.sales.currentWeekAvg.toLocaleString('uk-UA', {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                      })
                    : ''}
                </div>
              </Tooltip>
              <Tooltip
                title={
                  <div
                    style={{ whiteSpace: 'pre-line', textAlign: 'center' }}
                  >{`Срдн. реал. по ${fuelType} за\nпопер. день`}</div>
                }
                placement="top"
                enterDelay={200}
                leaveDelay={100}
              >
                <div className="regional-sales-value">
                  {fuelPrice.sales.previousDayAvg
                    ? fuelPrice.sales.previousDayAvg.toLocaleString('uk-UA', {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                      })
                    : ''}
                </div>
              </Tooltip>
            </div>
            <div className="regional-sales-right">
              <Tooltip
                title={
                  <div
                    style={{ whiteSpace: 'pre-line', textAlign: 'center' }}
                  >{`Приріст ${fuelType} поточного до\nминулого тижня`}</div>
                }
                placement="top"
                enterDelay={200}
                leaveDelay={100}
              >
                <div
                  className={`regional-sales-growth ${(weekGrowth ?? 0) > 0 ? 'positive-change' : (weekGrowth ?? 0) < 0 ? 'negative-change' : ''}`}
                >
                  {weekGrowth != null && !!fuelPrice.sales.currentWeekAvg ? (
                    <span className={`${weekGrowth > 0 ? 'positive-change' : weekGrowth < 0 ? 'negative-change' : ''}`}>
                      <span>
                        {weekGrowth >= 0 ? '+' : ''}
                        {weekGrowth.toFixed(1)}%
                      </span>
                      <span className={`price-arrow ${weekGrowth > 0 ? 'up' : weekGrowth < 0 ? 'down' : ''}`}>
                        {weekGrowth > 0 ? '↑' : weekGrowth < 0 ? '↓' : ''}
                      </span>
                    </span>
                  ) : (
                    ''
                  )}
                </div>
              </Tooltip>
              <Tooltip
                title={
                  <div
                    style={{ whiteSpace: 'pre-line', textAlign: 'center' }}
                  >{`Приріст ${fuelType} попер. дня\nдо відповід. дня мин.\nтижня`}</div>
                }
                placement="top"
                enterDelay={200}
                leaveDelay={100}
              >
                <div
                  className={`regional-sales-growth ${dayGrowth > 0 ? 'positive-change' : dayGrowth < 0 ? 'negative-change' : ''}`}
                >
                  {dayGrowth !== 0 ? (
                    <span className={`${dayGrowth > 0 ? 'positive-change' : dayGrowth < 0 ? 'negative-change' : ''}`}>
                      <span>
                        {dayGrowth >= 0 ? '+' : ''}
                        {dayGrowth.toFixed(1)}%
                      </span>
                      <span className={`price-arrow ${dayGrowth > 0 ? 'up' : dayGrowth < 0 ? 'down' : ''}`}>
                        {dayGrowth > 0 ? '↑' : dayGrowth < 0 ? '↓' : ''}
                      </span>
                    </span>
                  ) : (
                    ''
                  )}
                </div>
              </Tooltip>
            </div>
            </div>
          )}
        </div>
      ) : (
        <div
          className={`regional-price-value ${cellClass} ${priceChange === 'new' ? 'price-new' : priceChange === 'removed' ? 'price-removed' : ''}`}
          style={{ textAlign: 'center' }}
        >
          {priceChange === 'removed' ? (
            ''
          ) : (
            <>
              <span>{formatPrice(price)}</span>
              {priceChangeValue !== 0 && Math.abs(priceChangeValue) > 0.001 && (
                <span className={priceChangeValue >= 0 ? 'price-diff-positive' : 'price-diff-negative'}>
                  ({priceChangeValue >= 0 ? '+' : ''}
                  {priceChangeValue.toFixed(2)})
                  <span
                    className={priceChange === 'up' ? 'price-arrow up' : priceChange === 'down' ? 'price-arrow down' : ''}
                  >
                    {getPriceChangeIcon(priceChange)}
                  </span>
                </span>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
